/**
 * English locale catalog for the player-tags sub-domain
 * (`apps/frontend/src/features/gvg-planner/components/tags`).
 *
 * Each constant below is the canonical English shape; the matching Russian
 * catalog in `./ru.ts` must satisfy `typeof <enName>` exactly. Consumers pair
 * the catalog with `defineLocaleCatalog({ en, ru })` from `@/shared/i18n` and
 * resolve it through `useLocaleMessages(catalog)`.
 *
 * English is the source-of-truth shape — when a key disappears from `en`,
 * `ru` must drop the matching key or the type system will fail.
 *
 * Translation scope: this catalogue covers the tag-strip / catalog / filter
 * chrome — the input placeholders, panel headings, button labels, modal
 * copy, error fallbacks, and aria-labels. Tag names themselves are
 * persisted data (`PlayerTag.name`) and pass through verbatim via
 * `tagDisplayName(t.name)`. Player names pass through as
 * `playerDisplayName(player)`.
 */
export const playerTagsEn = {
  filterStrip: {
    toolbarLabel: 'Tag filter',
    filterRosterChip: 'Filter roster',
    clearFilterButton: 'Clear filter',
  },
  playerStrip: {
    toggleTitle: (tagName: string) => `Filter by tag '${tagName}'`,
    toggleAria: (tagName: string) => `Toggle filter for tag ${tagName}`,
    removeTitle: (tagName: string, playerLabel: string) =>
      `Remove tag ${tagName} from ${playerLabel}`,
    removeAria: (tagName: string, playerLabel: string) =>
      `Remove tag ${tagName} from ${playerLabel}`,
    addTitle: (playerLabel: string) => `Assign a tag to ${playerLabel}`,
    addPendingTitle: 'Assigning tag…',
    triggerLabelIdle: '+ Tag',
    triggerLabelPending: 'Assigning…',
    dropdownLabel: 'Available tags',
  },
  catalog: {
    heading: 'Player tags',
    description: 'Free-form chips you can pin to players (max 100 per guild).',
    resetHeading: 'Reset all tag assignments',
    resetDescription:
      'Removes all tag assignments and custom tags while preserving system map tags.',
    resetTitleNoPermissions: 'Only officers can reset tag assignments',
    resetTitlePending: 'Reset in progress…',
    resetTitleEmpty: 'Remove custom tags and clear all assignments',
    resetTitleWithAssignments: (count: number) =>
      `Delete all custom tags and remove ${String(count)} assignment${count === 1 ? '' : 's'}`,
    resetButtonIdle: 'Reset all tags',
    resetButtonPending: 'Resetting…',
    confirmTitle: 'Reset player tags?',
    confirmDestructiveLabel: 'Destructive action',
    confirmBody: (count: number) =>
      `This removes ${String(count)} assignment${count === 1 ? '' : 's'} and permanently deletes every custom tag. System map tags, including the 15 and 25 minute boss tags, are preserved.`,
    confirmCancel: 'Cancel',
    confirmDelete: 'Delete custom tags and reset',
    errorFallback: 'Could not reset tag assignments. Please try again.',
    emptyCatalog: 'No tags yet. Create one above and then pin it to a player from their card.',
    createInputPlaceholder: 'New tag name (e.g. ⚔️ sniper)',
    createInputLabel: 'New tag name',
    createColorLabel: 'New tag color',
    createButton: 'Create',
    emojiPickerLabel: 'Add emoji to new tag',
    rowColorLabel: (tagName: string) => `Change color for tag ${tagName}`,
    rowRenameLabel: (tagName: string) => `Rename tag ${tagName}`,
    rowDeleteLabel: (tagName: string) => `Delete tag ${tagName}`,
    rowRenameButton: 'Rename…',
    rowDeleteButton: 'Delete',
    renamePrompt: (currentName: string) => `Rename tag "${currentName}" to:`,
    deleteConfirmWithAssignments: (name: string, count: number) =>
      `Delete tag "${name}"? It will be removed from ${String(count)} player${count === 1 ? '' : 's'}.`,
    deleteConfirmNoAssignments: (name: string) => `Delete tag "${name}"?`,
    rowMeta: (color: string, count: number) =>
      `${color} · ${String(count)} player${count === 1 ? '' : 's'}`,
  },
}
